import type { FC } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { Box, Fade, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { ContentCopy, LockOutlined } from '@mui/icons-material'

import { HOME_CONTENT } from '../defaults'
import { MobileScreenshotFallback } from './MobileScreenshotFallback'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SECTION_HEADING_ID = 'event-inspector-demo-heading'

const DEMO_DATA = {
  // Event Context
  sessionMode: 'Safe',
  agentMode: 'Accept edits',
  hookType: 'Post Tool Use',
  tool: 'Edit',
  eventType: 'File Modification',
  timestamp: '2/15/2026, 3:42:18 PM',

  // Encryption
  algorithm: 'AES-256-GCM',
  keyVersion: '1',

  // Environment
  host: 'MacBook Pro',
  workingDir: '~/Projects/nexus-web',

  // Terminal Session
  tmuxSessionName: 'nexus-web',
  tmuxSessionId: '$1',
  tmuxPaneId: '%1',

  // Breadcrumb
  breadcrumb: 'MacBook Pro > Nexus Web > API Gateway',

  // Identifiers
  userExternalId: 'usr_7f3a9b2e-4d81-4c6f-b5a7-e9f2d8c1a0b3',
  hostId: 'hst_4d8e1c6f-9a2b-4e7d-8f3c-b1d5a6e9c4f2',
  sessionId: 'ses_b5c7d9a1-3e6f-4a2d-9c8b-f4e7d1a5b3c6',
  userPromptId: 'upi_e2f4a6c8-1b3d-4e5f-a7c9-d2b8f6e4a1c3',
  eventId: 'evt_9d1b3f5e-7a2c-4d6f-b8e1-f3c5a9d7b2e4',
} as const

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const EventInspectorDemoSection: FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  if (isMobile) {
    return (
      <MobileScreenshotFallback
        headingId={SECTION_HEADING_ID}
        title={HOME_CONTENT.eventInspector.title}
        subtitle={HOME_CONTENT.eventInspector.description}
        imageSrc={HOME_CONTENT.eventInspector.image}
        imageAlt={HOME_CONTENT.eventInspector.imageAlt}
      />
    )
  }

  return (
    <Box
      component="section"
      role="region"
      aria-labelledby={SECTION_HEADING_ID}
      sx={sectionContainerSx}
    >
      <Typography id={SECTION_HEADING_ID} component="h2" sx={sectionTitleSx}>
        {HOME_CONTENT.eventInspector.title}
      </Typography>
      <Typography sx={sectionSubtitleSx}>{HOME_CONTENT.eventInspector.description}</Typography>

      <Fade in={true} timeout={600}>
        <Box
          role="img"
          aria-label="Demonstration of the event details inspector showing event context, encryption status, environment, terminal session, and identifier metadata"
          sx={responsiveWrapperSx}
        >
          <Typography sx={visuallyHiddenSx}>
            A static replica of the event details inspector showing five organized sections: event
            context with session mode, agent mode, and tool information; end-to-end encryption
            status with AES-256-GCM algorithm; environment details with host and working directory;
            terminal session details; and copyable identifiers for user, host, session, and event.
          </Typography>

          <Box sx={chromeContainerSx}>
            {/* Title row: title + breadcrumb on left, close X on right */}
            <Box sx={titleBarSx}>
              <Box sx={titleRowSx}>
                <Box sx={titleContentSx}>
                  <Typography variant="h6" sx={dialogTitleSx}>
                    Event details
                  </Typography>
                  <Typography variant="body2" sx={breadcrumbSx}>
                    {DEMO_DATA.breadcrumb}
                  </Typography>
                </Box>

                <IconButton
                  size="small"
                  sx={closeIconButtonSx(theme)}
                  tabIndex={-1}
                  aria-hidden="true"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Controls row: copy + JSON buttons — below title, matching BaseDialog */}
              <Box sx={controlsRowSx}>
                <IconButton size="small" sx={passiveIconButtonSx} tabIndex={-1} aria-hidden="true">
                  <ContentCopy sx={controlIconSx} />
                </IconButton>
                <IconButton size="small" sx={passiveIconButtonSx} tabIndex={-1} aria-hidden="true">
                  <Box component="span" sx={jsonIconSx}>
                    {'{}'}
                  </Box>
                </IconButton>
              </Box>
            </Box>

            {/* Content area */}
            <Box sx={contentAreaSx}>
              {/* Event Context */}
              <Box sx={sectionSx}>
                <SectionHeader title="Event Context" />
                <DetailRow
                  label="Session mode"
                  value={DEMO_DATA.sessionMode}
                  valueColor="success"
                />
                <DetailRow label="Agent mode" value={DEMO_DATA.agentMode} />
                <DetailRow label="Hook type" value={DEMO_DATA.hookType} />
                <DetailRow label="Tool" value={DEMO_DATA.tool} />
                <DetailRow label="Event type" value={DEMO_DATA.eventType} />
                <DetailRow label="Timestamp" value={DEMO_DATA.timestamp} />
              </Box>

              {/* Encryption */}
              <Box sx={sectionSx}>
                <SectionHeader title="Encryption" />
                <Box sx={detailRowSx}>
                  <Typography component="span" sx={labelSx}>
                    Status
                  </Typography>
                  <Box sx={encryptionStatusContainerSx}>
                    <LockOutlined sx={lockIconSx} />
                    <Typography component="span" sx={valueSuccessSx}>
                      End-to-end encrypted
                    </Typography>
                  </Box>
                </Box>
                <DetailRow label="Algorithm" value={DEMO_DATA.algorithm} />
                <DetailRow label="Key version" value={DEMO_DATA.keyVersion} />
              </Box>

              {/* Environment */}
              <Box sx={sectionSx}>
                <SectionHeader title="Environment" />
                <DetailRow label="Host" value={DEMO_DATA.host} />
                <DetailRow label="Working dir" value={DEMO_DATA.workingDir} />
              </Box>

              {/* Terminal Session */}
              <Box sx={sectionSx}>
                <SectionHeader title="Terminal Session" />
                <IdentifierRow label="Session name" value={DEMO_DATA.tmuxSessionName} />
                <IdentifierRow label="Session ID" value={DEMO_DATA.tmuxSessionId} />
                <IdentifierRow label="Pane ID" value={DEMO_DATA.tmuxPaneId} />
              </Box>

              {/* Identifiers */}
              <Box sx={sectionSx}>
                <SectionHeader title="Identifiers" />
                <IdentifierRow label="User External ID" value={DEMO_DATA.userExternalId} />
                <IdentifierRow label="Host ID" value={DEMO_DATA.hostId} />
                <IdentifierRow label="Session ID" value={DEMO_DATA.sessionId} />
                <IdentifierRow label="User Prompt ID" value={DEMO_DATA.userPromptId} />
                <IdentifierRow label="Event ID" value={DEMO_DATA.eventId} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Private — Sub-components
// ---------------------------------------------------------------------------

type ValueColor = 'success' | 'warning' | 'default'

interface SectionHeaderProps {
  readonly title: string
}

const SectionHeader: FC<SectionHeaderProps> = ({ title }) => (
  <Typography variant="overline" sx={sectionHeaderSx}>
    {title}
  </Typography>
)

interface DetailRowProps {
  readonly label: string
  readonly value: string
  readonly valueColor?: ValueColor
}

const DetailRow: FC<DetailRowProps> = ({ label, value, valueColor = 'default' }) => {
  const resolvedValueSx = resolveValueSx(valueColor)

  return (
    <Box sx={detailRowSx}>
      <Typography component="span" sx={labelSx}>
        {label}
      </Typography>
      <Typography component="span" sx={resolvedValueSx}>
        {value}
      </Typography>
    </Box>
  )
}

interface IdentifierRowProps {
  readonly label: string
  readonly value: string
}

const IdentifierRow: FC<IdentifierRowProps> = ({ label, value }) => (
  <Box sx={detailRowSx}>
    <Typography component="span" sx={labelSx}>
      {label}
    </Typography>
    <Typography component="span" sx={clickableValueSx}>
      {value}
    </Typography>
  </Box>
)

function resolveValueSx(valueColor: ValueColor): SxProps<Theme> {
  switch (valueColor) {
    case 'success':
      return valueSuccessSx
    case 'warning':
      return valueWarningSx
    default:
      return valueSx
  }
}

// ---------------------------------------------------------------------------
// Styles — Section wrapper
// ---------------------------------------------------------------------------

const sectionContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  py: { xs: 4, md: 6 },
  px: { xs: 2, md: 4 },
}

const sectionTitleSx: SxProps<Theme> = {
  color: 'text.primary',
  fontSize: { xs: '1.5rem', md: '1.75rem' },
  fontWeight: 600,
  textAlign: 'center',
  mb: 1,
}

const sectionSubtitleSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: { xs: '1rem', md: '1.1rem' },
  fontWeight: 400,
  textAlign: 'center',
  mb: 2,
}

// ---------------------------------------------------------------------------
// Styles — Chrome container
// ---------------------------------------------------------------------------

const responsiveWrapperSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
}

const chromeContainerSx: SxProps<Theme> = (theme) => ({
  borderRadius: 2,
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(62, 39, 35, 0.10)',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 4px 24px rgba(0, 0, 0, 0.3)'
      : '0 4px 24px rgba(0, 0, 0, 0.08)',
  overflow: 'hidden',
  backgroundColor: 'background.paper',
  width: '100%',
  maxWidth: 580,
})

// ---------------------------------------------------------------------------
// Styles — Title bar (matching BaseDialog layout)
// ---------------------------------------------------------------------------

const titleBarSx: SxProps<Theme> = (theme) => ({
  pt: 2,
  pb: 1.5,
  px: 2.5,
  borderBottom:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(62, 39, 35, 0.08)',
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(62, 39, 35, 0.02)',
})

const titleRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  mb: 1.5,
}

const titleContentSx: SxProps<Theme> = {
  flex: 1,
  pr: 2,
}

const dialogTitleSx: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: '1.1rem',
  mb: 0.25,
}

const breadcrumbSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: '0.8rem',
  fontWeight: 400,
  lineHeight: 1.4,
}

const controlsRowSx: SxProps<Theme> = {
  display: 'flex',
  gap: 0.5,
}

const passiveIconButtonSx: SxProps<Theme> = {
  color: 'text.secondary',
  cursor: 'default',
  '&:hover': { backgroundColor: 'transparent' },
}

const closeIconButtonSx = (theme: Theme): SxProps<Theme> => ({
  color: theme.palette.error.main,
  width: 32,
  height: 32,
  cursor: 'default',
  '&:hover': { backgroundColor: 'transparent' },
})

const controlIconSx: SxProps<Theme> = {
  fontSize: '1.1rem',
}

const jsonIconSx: SxProps<Theme> = {
  fontFamily: 'monospace',
  fontSize: '1.15rem',
  fontWeight: 700,
  color: 'text.primary',
}

// ---------------------------------------------------------------------------
// Styles — Content area (tighter than prod for compact demo)
// ---------------------------------------------------------------------------

const contentAreaSx: SxProps<Theme> = {
  pt: 2,
  px: 2.5,
  pb: 2,
}

const sectionSx: SxProps<Theme> = {
  mb: 1.5,
}

const sectionHeaderSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: '0.65rem',
  fontWeight: 600,
  letterSpacing: '0.08em',
  mb: 0.5,
  display: 'block',
}

const detailRowSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: '120px 1fr',
  gap: 1.5,
  mb: 0.25,
}

const labelSx: SxProps<Theme> = {
  fontFamily: 'monospace',
  fontSize: '0.75rem',
  color: 'text.secondary',
}

const valueSx: SxProps<Theme> = {
  fontFamily: 'monospace',
  fontSize: '0.75rem',
  color: 'text.primary',
}

const valueSuccessSx: SxProps<Theme> = {
  fontFamily: 'monospace',
  fontSize: '0.75rem',
  color: 'success.main',
}

const valueWarningSx: SxProps<Theme> = {
  fontFamily: 'monospace',
  fontSize: '0.75rem',
  color: 'warning.main',
}

const clickableValueSx: SxProps<Theme> = {
  fontFamily: 'monospace',
  fontSize: '0.75rem',
  color: 'primary.main',
  opacity: 0.5,
  cursor: 'pointer',
  transition: 'opacity 0.2s',
  '&:hover': {
    opacity: 1,
  },
}

const encryptionStatusContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
}

const lockIconSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  color: 'success.main',
}

// ---------------------------------------------------------------------------
// Styles — Accessibility
// ---------------------------------------------------------------------------

const visuallyHiddenSx: SxProps<Theme> = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
}
